import React, {useEffect, useState} from "react";
import {NumericFormat} from 'react-number-format';
import {format} from "date-fns";
import {es} from "date-fns/locale";
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Typography,
    Badge,
    Select,
    Option,
    Tooltip,
    CardFooter,
    TimelineItem,
    TimelineHeader,
    TimelineIcon,
    TimelineConnector,
    Timeline,
    TimelineBody,
    IconButton, Textarea,
} from "@material-tailwind/react";
import {
    PrinterIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    QuestionMarkCircleIcon,
    ArrowsUpDownIcon,
} from "@heroicons/react/24/solid";
import InputMask from "react-input-mask";
import MapComponent from "../shared/map_component";
import {InformationCircleIcon} from "@heroicons/react/16/solid";
import {XMarkIcon} from "@heroicons/react/24/outline";

const Lots = ({role}) => {
    const [step, setStep] = useState(1);
    const [searchType, setSearchType] = useState("search_by_name");
    const [clavePredio, setClavePredio] = useState("");
    const [name, setName] = useState("");
    const [pLastName, setPLastName] = useState("");
    const [mLastName, setMLastName] = useState("");
    const [selectedBAdeudos, setSelectedBAdeudos] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [contributors, setContributors] = useState(null);
    const [selectedContributor, setSelectedContributor] = useState(null);
    const [lots, setLots] = useState([]);
    const [predio, setPredio] = useState(null);
    const [error, setError] = useState(null);
    const [adeudos, setAdeudos] = useState(null);
    const [histories, setHistories] = useState([]);
    const [notes, setNotes] = useState('');
    const [notesError, setNotesError] = useState('');
    const [expiredDebts, setExpiredDebts] = useState(null);

    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [openInvoicesDialog, setOpenInvoicesDialog] = useState(false);
    const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
    const [openDialogPre, setOpenDialogPre] = useState(false);
    const [openEndDialog, setOpenEndDialog] = useState(false);

    const totalSelected = selectedBAdeudos.reduce(
        (acc, adeudo) => acc + adeudo.total,
        0
    );
    const totalSelectedRounded = Math.ceil(totalSelected);

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    useEffect(() => {
        if (adeudos) {
            const currentYear = new Date().getFullYear();
            const expiredBimesters = adeudos.filter((item) => item.year <= (currentYear - 5));
            const expiredYears = expiredBimesters.reduce((acc, item) => {
                const year = item.year;
                if (!acc[year]) {
                    acc[year] = {
                        year: year,
                        tax: 0,
                        university_tax: 0,
                        surcharge: 0,
                        subtotal: 0,
                        discount: 0,
                        total: 0,
                    };
                }
                acc[year].tax += item.tax;
                acc[year].university_tax += item.university_tax;
                acc[year].surcharge += item.surcharge;
                acc[year].subtotal += item.subtotal;
                acc[year].discount += item.discount;
                acc[year].total += item.total;
                return acc;
            }, {});

            if(Object.values(expiredYears).length > 0) {
                setExpiredDebts(Object.values(expiredYears));
            }
        }
    }, [adeudos]);

    const handleBuscar = async () => {
        setError(null);
        setPredio(null);
        setLots([]);
        setSelectedContributor(null);

        if (searchType === "search_by_cve") {
            if (clavePredio.trim() === "") return setError("Ingrese una clave válida");

            await handleSelectPredio(clavePredio);
        } else if (searchType === "search_by_name") {
            if (name.trim() === "" && pLastName.trim() === "" && mLastName.trim() === "") return alert("Ingrese un nombre válido");

            try {
                const response = await fetch(`/lots/search_by_name?name=${name}&parental_last_name=${pLastName}&maternal_last_name=${mLastName}`);

                if (!response.ok) {
                    setError("No se encontraron contribuyentes.");
                    setStep(1);
                    return;
                }

                const data = await response.json();
                setContributors(data);
            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }
    };

    const handleGetLotsByContributor = async (contributorId) => {
        try {
            const response = await fetch(`/lots/search_by_contributor?id=${contributorId}`);
            const data = await response.json();
            setLots(data);
            setSelectedContributor(contributorId);
        } catch (err) {
            console.error("Error fetching lots:", err);
        }
    };

    const fetchInvoices = async () => {
        try {
            const response = await fetch(`related_invoices/${clavePredio}`);
            const data = await response.json();
            setInvoices(data);
        } catch (error) {
            console.error("Error fetching invoices:", error);
        }
    };

    const handleOpenModal = () => {
        setOpenInvoicesDialog(true);
        fetchInvoices();
    };

    const handleReprint = (invoiceId) => {
        window.open(`/print_ticket/${invoiceId}`, "_blank");
    };

    const handleReset = () => {
        setStep(1);
        setClavePredio("");
        setSelectedBAdeudos([]);
        setName("");
        setPLastName("");
        setMLastName("");
        setOpenResetDialog(false);
        setLots([]);
        setAdeudos(null);
        setError(null);
        setContributors(null);
        setSelectedContributor(null);
        setPredio(null);
        setOpenHistoryDialog(false);
        setHistories([]);
        setNotes('');
        setNotesError('');
        setExpiredDebts(null);
        setOpenDialogPre(false);
        setOpenEndDialog(false);
    };

    const toggleAdeudo = (adeudo) => {
        setSelectedBAdeudos((prev) => {
            const isSelected = prev.some((item) => item.id === adeudo.id);

            if (isSelected) {
                return prev.filter((item) => item.id_debt < adeudo.id_debt);
            } else {
                const affectedAdeudos = adeudos.filter(
                    (item) => item.id_debt <= adeudo.id_debt
                );
                return [...affectedAdeudos];
            }
        });
    };
    const handleSelectPredio = async (parcel_code) => {
        setClavePredio(parcel_code);

        try {
            const [lotsResponse, debtsResponse] = await Promise.all([
                fetch(`/lots/${parcel_code}/search_by_code.json`),
                fetch(`/lots/${parcel_code}/debts.json`)
            ]);

            if (!lotsResponse.ok) {
                setError("No se encontraron datos para este código.");
                setStep(1);
                return;
            } else {

            }
            const lotsData = await lotsResponse.json();
            setPredio(lotsData);

            if (debtsResponse.ok) {
                const debtsData = await debtsResponse.json();

                setAdeudos(debtsData);
                setSelectedBAdeudos(debtsData);
            } else {
                console.log("No se encontraron datos para este código.");
            }

        } catch (err) {
            console.error("Error al buscar el predio:", err);
        }

        setStep(2);
    };

    const handleToggleReview = async () => {
        try {
            const response = await fetch(`/lots/${clavePredio}/requires_review`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                console.log("Error toggle requires review");
            }

            const data = await response.json();
            setPredio({
                ...predio,
                requires_review: data.requires_review
            })
        } catch (error) {
            console.error("Error toggle requires review: ", error);
        }
    };

    const fetchHistory = async () => {
        try {
            const response = await fetch(`lots/${clavePredio}/history`);
            const data = await response.json();
            setHistories(data);
        } catch (error) {
            console.error("Error fetching invoices:", error);
        }
    };

    const handleOpenHistoryModal = () => {
        setOpenHistoryDialog(true);
        fetchHistory();
    };

    const openDialogExpiredDebt = () => {
        setOpenDialogPre(true);
    }

    const handleDebtExpiration = async () => {
        if (notes) {
            const response = await fetch(`/debts/dismiss_debt`, {
                method: "POST",
                body: JSON.stringify({
                    lot: predio.parcel_code,
                    notes: notes
                }),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCSRFToken(),
                },
            });

            if (!response.ok) {
                setNotesError('A ocurrido un error con la solicitud.');
            } else {
                setExpiredDebts(null);
                setOpenDialogPre(false);
                setOpenEndDialog(true);
                await handleSelectPredio(clavePredio);
            }
        } else {
            setNotesError('Es necesario ingresar el motivo de la prescripción');
        }
    }

    return (
        <>
            <div className="min-h-screen pb-5">
                <div className="w-full bg-white border-gray-200 px-3 py-6 sm:px-5 sm:py-10 text-gray-800">
                    {step === 1 && (
                        <>
                            <h6 className="font-semibold uppercase text-gray-600 mb-4">
                                PREDIOS
                            </h6>
                            <div className="mb-6">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center bg-gray-100 p-4 rounded">
                                    <span className="text-lg font-bold whitespace-nowrap sm:pr-4">Búsqueda por:</span>
                                    <Select
                                        value={searchType}
                                        onChange={(e) => setSearchType(e)}
                                        className="w-full"
                                    >
                                        <Option value="search_by_cve">Clave catastral</Option>
                                        <Option value="search_by_name">Nombre y Apellidos</Option>
                                    </Select>
                                </div>
                                <div className="mt-4 bg-white p-4 sm:p-6 rounded shadow-md">
                                    {searchType === "search_by_cve" && (
                                        <section id="find_by_cve">
                                            <div
                                                className="flex justify-between md:items-center gap-y-4 flex-col md:flex-row">
                                                <div>
                                                    <h1 className="font-bold text-lg">BUSQUEDA POR PREDIO</h1>
                                                    <p className="text-sm text-gray-500">
                                                        Ingresa la clave catastral para buscar.
                                                    </p>
                                                    {error && <p style={{color: "red"}}>{error}</p>}
                                                </div>
                                                <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto">
                                                    <div className="w-full sm:w-auto lg:w-96">

                                                        <InputMask
                                                            mask="519999-999-999"
                                                            value={clavePredio}
                                                            onChange={(e) => setClavePredio(e.target.value)}
                                                        >
                                                            {(inputProps) => (
                                                                <input
                                                                    {...inputProps}
                                                                    type="text"
                                                                    placeholder="51XXXX-XXX-XXX"
                                                                    className="p-2 w-full border border-gray-300 rounded"
                                                                />
                                                            )}
                                                        </InputMask>
                                                    </div>
                                                    <Button className="w-full sm:w-auto" onClick={handleBuscar}>Buscar</Button>
                                                </div>
                                            </div>
                                        </section>
                                    )}
                                    {searchType === "search_by_name" && (
                                        <>
                                            <section id="find_by_name">
                                                <div
                                                    className="flex justify-between md:items-center gap-y-4 flex-col md:flex-row">
                                                    <div>
                                                        <h1 className="font-bold text-lg">BUSQUEDA POR NOMBRE</h1>
                                                        <p className="text-sm text-gray-500">
                                                            Ingresa los datos del predio para buscar.
                                                        </p>
                                                        {error && <p style={{color: "red"}}>{error}</p>}
                                                    </div>
                                                    <div className="flex flex-col gap-2 w-full sm:flex-row sm:flex-wrap sm:items-center sm:w-auto">
                                                        <input
                                                            type="text"
                                                            placeholder="Nombre"
                                                            className="p-2 w-full sm:w-28 md:w-36 border border-gray-300 rounded uppercase"
                                                            value={name}
                                                            onChange={(e) => setName(e.target.value.toUpperCase())}
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Apellido Paterno"
                                                            className="p-2 w-full sm:w-28 md:w-36 border border-gray-300 rounded uppercase"
                                                            value={pLastName}
                                                            onChange={(e) => setPLastName(e.target.value.toUpperCase())}
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Apellido Materno"
                                                            className="p-2 w-full sm:w-28 md:w-36 border border-gray-300 rounded uppercase"
                                                            value={mLastName}
                                                            onChange={(e) => setMLastName(e.target.value.toUpperCase())}
                                                        />
                                                        <Button className="w-full sm:w-auto" onClick={handleBuscar}>Buscar</Button>
                                                    </div>
                                                </div>
                                            </section>
                                        </>
                                    )}
                                </div>
                                {searchType === "search_by_name" && contributors && (
                                    <div className="mt-8">
                                        {contributors.length > 0 ? (
                                            <div>
                                                <h6 className="font-semibold uppercase text-gray-600 mb-4">
                                                    Resultados de búsqueda
                                                </h6>

                                                <ul className="border p-4 rounded-md bg-white shadow">
                                                    {contributors.map((contributor, index) => (
                                                        <li key={contributor.id}
                                                            className={`${index === contributors.length - 1 ? "" : "border-b"}`}>
                                                            <div
                                                                className="flex justify-between items-center py-3 px-4 rounded-md cursor-pointer hover:bg-blue-gray-50 transition duration-200"
                                                                onClick={() => handleGetLotsByContributor(contributor.id)}
                                                            >
                                                                    <span className="text-gray-700 font-medium">
                                                                        <strong>
                                                                          {contributor.name} {contributor.parental_last_name} {contributor.maternal_last_name}
                                                                        </strong>
                                                                    </span>
                                                                {selectedContributor === contributor.id ? (
                                                                    <ChevronDownIcon
                                                                        className="h-5 w-5 text-gray-700"/>
                                                                ) : (
                                                                    <ChevronRightIcon
                                                                        className="h-5 w-5 text-gray-500"/>
                                                                )}
                                                            </div>
                                                            {selectedContributor === contributor.id && (
                                                                <ul className="p-4 mt-2 bg-blue-gray-500/20 rounded-md">
                                                                    {lots.length > 0 ? (
                                                                        lots.map((lot, index) => (
                                                                            <li
                                                                                key={`${lot.parcel_code}-${index}`}
                                                                                className={`flex justify-between items-center py-2 px-3 rounded-md cursor-pointer hover:bg-gray-100 transition duration-200 hover:bg-brown-500/10 ${index === lots.length - 1 ? "" : "border-b border-blue-gray-50"}`}
                                                                                onClick={() => handleSelectPredio(lot.parcel_code)}
                                                                            >
                                                                                  <span className="text-gray-600">
                                                                                    <strong>{lot.parcel_code}</strong>: {`${lot.road_name}${lot.ext_num ? ' #' + lot.ext_num : ''}, ${lot.neighborhood_name}`}
                                                                                  </span>
                                                                            </li>
                                                                        ))
                                                                    ) : (
                                                                        <li className="text-gray-500 italic">No se
                                                                            encontraron <strong>predios.</strong>
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <div>
                                                <h6 className="font-semibold uppercase text-gray-600 my-4 text-center">
                                                    No se encontraron coincidencias.
                                                </h6>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {step !== 1 && (
                        <>
                            <h6 className="font-semibold uppercase text-gray-600 mb-2">
                                PREDIO
                            </h6>
                            <div className="bg-white p-5 border rounded-md mb-4">
                                <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center">
                                    <div>
                                        <h6 className="font-semibold">Clave Buscada:</h6>
                                        <p>{clavePredio}</p>
                                    </div>
                                    <div className="flex gap-2 items-center md:ml-auto">
                                        <Button
                                            size="sm"
                                            className="mt-2 bg-blue-500 w-full md:w-auto"
                                            onClick={() => handleSelectPredio(clavePredio)}
                                        >
                                            Recargar
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="mt-2 bg-red-700 w-full md:w-auto"
                                            onClick={() => setOpenResetDialog(true)}
                                        >
                                            Restablecer
                                        </Button>
                                    </div>
                                </div>

                                <Dialog open={openResetDialog} handler={setOpenResetDialog}>
                                    <DialogHeader>Confirmación</DialogHeader>
                                    <DialogBody>¿Estás seguro de restablecer?</DialogBody>
                                    <DialogFooter>
                                        <Button
                                            variant="text"
                                            onClick={() => setOpenResetDialog(false)}
                                            className="mr-2"
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            className="bg-red-500 text-white"
                                            onClick={handleReset}
                                        >
                                            Confirmar
                                        </Button>
                                    </DialogFooter>
                                </Dialog>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <div>
                            <h6 className="font-semibold uppercase text-gray-600 mt-8">
                                Información del predio
                            </h6>
                            <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                                <div className="flex-grow border p-4 rounded-md w-full lg:w-1/2">
                                    {predio && (
                                        <div className="overflow-x-auto">
                                        <table className="w-full min-w-max table-auto text-left">
                                            <tbody>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Tipo de Predio:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.lot_location_type}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Propietario:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.name} {predio.parental_last_name} {predio.maternal_last_name}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Dirección:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.road_name}{predio.ext_num ? " #" + predio.ext_num : ""}, {predio.neighborhood_name},
                                                        C.P. {predio.postal_code}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Referencias:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.address_references === null ? '' : predio.address_references}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Sup. Terreno:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.land_area.toFixed(2)}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Valor Terreno:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        <NumericFormat
                                                            value={predio.land_value.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Sup. Const:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.construction_area.toFixed(2)}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Valor Const:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        <NumericFormat
                                                            value={predio.construction_value.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Instalaciones Especiales:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <div className='flex flex-1 gap-1'>
                                                        <Typography
                                                            variant="small"
                                                            className="font-normal text-gray-600"
                                                        >
                                                            <NumericFormat
                                                                value={predio.special_facilities_value.toFixed(2)}
                                                                displayType={'text'}
                                                                prefix={'$'}
                                                                allowLeadingZeros
                                                                thousandSeparator=","
                                                            />
                                                        </Typography>
                                                        {predio.special_facilities &&
                                                            <Tooltip
                                                                content={
                                                                    <div className="w-80">
                                                                        <Typography
                                                                            variant="small"
                                                                            color="white"
                                                                            className="font-normal opacity-80"
                                                                        >
                                                                            {predio.special_facilities}
                                                                        </Typography>
                                                                    </div>
                                                                }
                                                                placement='right'
                                                                className='!z-[9999]'
                                                            >
                                                                <InformationCircleIcon
                                                                    className="h-4 w-4" color='gray-400'/>
                                                            </Tooltip>
                                                        }
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Valor Catastral:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        <NumericFormat
                                                            value={predio.qualification.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Tasa:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        {predio.rate}
                                                    </Typography>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-bold"
                                                    >
                                                        Impuesto:
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <Typography
                                                        variant="small"
                                                        className="font-normal text-gray-600"
                                                    >
                                                        <NumericFormat
                                                            value={predio.tax.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </Typography>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        </div>
                                    )}

                                    <CardFooter className="p-0 flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
                                        <Button
                                            size="sm"
                                            className={`mt-2 flex items-center justify-center bg-blue-gray-500`}
                                            onClick={handleOpenHistoryModal}
                                        >
                                            Historial
                                        </Button>
                                        <Button
                                            size="sm"
                                            className={`mt-2 flex items-center justify-center ${predio.requires_review ? "btn-review-active bg-red-700" : "bg-amber-500"}`}
                                            onClick={handleToggleReview}
                                        >
                                            {predio.requires_review ? "Marcado para revisión" : "Marcar para revisión"}
                                        </Button>
                                    </CardFooter>
                                </div>
                                <div className="flex-grow border rounded-md w-full lg:w-1/2 min-h-[350px]">
                                    {predio.latitude && predio.longitude ? (
                                        <MapComponent
                                            parcel_code={clavePredio}
                                            latitude={predio.latitude}
                                            longitude={predio.longitude}
                                        />
                                    ) : (
                                        <MapComponent/>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2 mt-8 md:flex-row md:justify-between md:items-center">
                                <h6 className="font-semibold uppercase text-gray-600">
                                    Adeudos
                                </h6>

                                <div className="flex flex-col gap-2 md:flex-row md:items-center md:space-x-2">
                                    {(role === 'god' || role === 'admin') && expiredDebts && (
                                        <Button
                                            size="sm"
                                            className="bg-green-500 flex items-center justify-center md:mx-2"
                                            onClick={openDialogExpiredDebt}
                                        >
                                            Prescripción
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        className="bg-amber-500 flex items-center justify-center md:mx-2"
                                        onClick={() => {
                                            window.open("/print_debts/" + clavePredio + ".pdf");
                                        }}
                                    >
                                        Estado de cuenta
                                    </Button>

                                    <Button
                                        size="sm"
                                        className="bg-blue-gray-500 flex items-center justify-center md:mx-2"
                                        onClick={handleOpenModal}
                                    >
                                        Reimprimir tickets
                                    </Button>
                                </div>
                            </div>

                            <Dialog open={openHistoryDialog} handler={() => setOpenHistoryDialog(false)}
                                    size={history.length > 0 ? 'xxl' : 'md'}>
                                <DialogHeader className="justify-between">
                                    Historial del predio
                                    <IconButton
                                        color="blue-gray"
                                        size="sm"
                                        variant="text"
                                        onClick={() => setOpenHistoryDialog(false)}
                                    >
                                        <XMarkIcon className='w-full' />
                                    </IconButton>
                                </DialogHeader>
                                <DialogBody
                                    className="overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-xl [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-xl [&::-webkit-scrollbar-track]:bg-slate-100">
                                    {histories.length > 0 ? (
                                        <Timeline>
                                            {histories.map((history, index) => (
                                                <TimelineItem key={index}>
                                                    <TimelineConnector/>
                                                    <TimelineHeader>
                                                        <TimelineIcon className="p-2">
                                                            <ArrowsUpDownIcon className="h-4 w-4"/>
                                                        </TimelineIcon>
                                                        <div className="flex flex-col">
                                                            <div className='flex gap-1 flex-1'>
                                                                <Typography variant="h6" color="blue-gray"
                                                                            className="leading-none">
                                                                    {history.reason_type_id} - {history.description}
                                                                </Typography>
                                                                <Tooltip
                                                                    content={
                                                                        <div className="w-80">
                                                                            <Typography color="white" className="font-medium">
                                                                                Notas del movimiento
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="small"
                                                                                color="white"
                                                                                className="font-normal opacity-80"
                                                                            >
                                                                                {history.notes}
                                                                            </Typography>
                                                                        </div>
                                                                    }
                                                                    placement='right'
                                                                    className='!z-[9999]'
                                                                >
                                                                    <InformationCircleIcon
                                                                        className="h-4 w-4" color='gray-400'/>
                                                                </Tooltip>
                                                            </div>
                                                            <Typography color="gray" variant='small'>
                                                                {format(history.created_at, 'PPP hh:mm', {locale: es})} - {history.user_last_update}
                                                            </Typography>
                                                        </div>
                                                    </TimelineHeader>
                                                    <TimelineBody className="-mt-2 pb-8 w-full">
                                                        <Typography color="gray" className='pt-1'>
                                                            {history.contributor_name} {history.contributor_parental_last_name} {history.contributor_maternal_last_name}
                                                        </Typography>
                                                        <div className="overflow-x-auto">
                                        <table className="min-w-full w-full table-auto border-collapse">
                                                            <thead>
                                                            <tr>
                                                                {["Sup. Constr.", "Val. Constr.", "Sup. Terreno", "Val. Terreno", "Inst. Especiales", "Val. Catastral", "Tasa", "Impuesto"].map(
                                                                    (el) => (
                                                                        <th
                                                                            key={el}
                                                                            className="border-b border-blue-gray-50 py-1 px-4 text-left"
                                                                        >
                                                                            <Typography
                                                                                variant="small"
                                                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                            >
                                                                                {el}
                                                                            </Typography>
                                                                        </th>
                                                                    )
                                                                )}
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            <tr>
                                                                <td className='py-2 px-4'>{history.construction_area}</td>
                                                                <td className='py-2 px-4'>
                                                                    <NumericFormat
                                                                        value={history.construction_value.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                                <td className='py-2 px-4'>{history.land_area}</td>
                                                                <td className='py-2 px-4'>
                                                                    <NumericFormat
                                                                        value={history.land_value.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                                <td className='py-2 px-4'>
                                                                    <div className='flex flex-1 gap-1'>
                                                                        <NumericFormat
                                                                            value={history.special_facilities_value.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                        {history.special_facilities &&
                                                                            <Tooltip
                                                                                content={
                                                                                    <div className="w-80">
                                                                                        <Typography color="white"
                                                                                                    className="font-medium">
                                                                                            Instalaciones Especiales
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="small"
                                                                                            color="white"
                                                                                            className="font-normal opacity-80"
                                                                                        >
                                                                                            {history.special_facilities}
                                                                                        </Typography>
                                                                                    </div>
                                                                                }
                                                                                className='!z-[9999]'
                                                                            >
                                                                                <InformationCircleIcon
                                                                                    className="h-4 w-4"/>
                                                                            </Tooltip>
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td className='py-2 px-4'>
                                                                    <NumericFormat
                                                                        value={history.qualification.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                                <td className='py-2 px-4'>{history.rate}</td>
                                                                <td className='py-2 px-4'>
                                                                    <NumericFormat
                                                                        value={history.tax.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                            </tr>
                                                            </tbody>
                                                        </table>
                                                        </div>
                                                    </TimelineBody>
                                                </TimelineItem>
                                            ))}
                                        </Timeline>
                                    ) : (
                                        <Typography color="gray">No se encontró historial del predio.</Typography>
                                    )}
                                </DialogBody>
                                <DialogFooter>
                                    <Button color="red" onClick={() => setOpenHistoryDialog(false)}>
                                        Cerrar
                                    </Button>
                                </DialogFooter>
                            </Dialog>

                            <Dialog open={openInvoicesDialog} handler={() => setOpenInvoicesDialog(false)}>
                                <DialogHeader>Facturas Anteriores</DialogHeader>
                                <DialogBody
                                    className="max-h-60 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-xl [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-xl [&::-webkit-scrollbar-track]:bg-slate-100">
                                    {invoices.length > 0 ? (
                                        <div className="overflow-x-auto">
                                        <table className="min-w-full w-full table-auto border-collapse">
                                            <thead>
                                            <tr>
                                                <th className="py-2 px-4 text-left font-semibold">Fecha</th>
                                                <th className="py-2 px-4 text-left font-semibold">Estatus</th>
                                                <th className="py-2 px-4 text-left font-semibold">Total</th>
                                                <th className="py-2 px-4 text-right font-semibold"></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {invoices.map(({id, created_at, status, total}) => (
                                                <tr key={id} className="border-b">
                                                    <td className="py-2 px-4 font-bold">{new Date(created_at).toISOString().split('T')[0]}</td>
                                                    <td className="py-2 px-4 font-bold">{status}</td>
                                                    <td className="py-2 px-4">
                                                        <NumericFormat
                                                            value={total.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </td>
                                                    <td className="py-2 px-4 flex justify-end">
                                                        <Button
                                                            size="sm"
                                                            color="blue-gray"
                                                            onClick={() => handleReprint(id)}
                                                        >
                                                            <PrinterIcon className="w-5 h-5"/>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                        </div>
                                    ) : (
                                        <Typography color="gray">No se encontraron facturas.</Typography>
                                    )}
                                </DialogBody>
                                <DialogFooter>
                                    <Button color="red" onClick={() => setOpenInvoicesDialog(false)}>
                                        Cerrar
                                    </Button>
                                </DialogFooter>
                            </Dialog>

                            <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px] table-auto">
                                <thead>
                                <tr>
                                    {["", "Año", "Bimestre", "Impuesto", "Imp. Univ.", "Recargos", "Subtotal", "Descuento", "Total"].map(
                                        (el) => (
                                            <th
                                                key={el}
                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                            >
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    {el}
                                                </Typography>
                                            </th>
                                        )
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {(!adeudos || adeudos.length === 0) ? (
                                    <tr>
                                        <td colSpan="100%" className="text-center">
                                            <Typography
                                                variant="paragraph"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                El predio no tiene adeudos.
                                            </Typography>
                                        </td>
                                    </tr>
                                ) : (adeudos.map((adeudo, index) => {
                                        const className = `py-3 px-5 ${
                                            index === adeudos.length - 1 ? "" : "border-b border-blue-gray-50"
                                        }`;
                                        const disabled = index === 0;
                                        return (
                                            <tr key={adeudo.id} className="hover:bg-gray-50 cursor-pointer">
                                                <td className={className}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBAdeudos.some(
                                                            (item) => item.id === adeudo.id
                                                        )}
                                                        onChange={() => toggleAdeudo(adeudo)}
                                                        disabled={disabled}
                                                    />
                                                </td>
                                                <td className={className}>{adeudo.year}</td>
                                                <td className={className}>{adeudo.bimester}</td>
                                                <td className={className}>${adeudo.tax.toFixed(2)}</td>
                                                <td className={className}>${adeudo.university_tax.toFixed(2)}</td>
                                                <td className={className}>${adeudo.surcharge.toFixed(2)}</td>
                                                <td className={className}>${adeudo.subtotal.toFixed(2)}</td>
                                                <td className={className}>
                                                    <Tooltip content={`${adeudo.discount_percentage}%`}
                                                             placement="right">
                                                        <span>${adeudo.discount.toFixed(2)}</span>
                                                    </Tooltip>
                                                </td>
                                                <td className={className}>${(adeudo.total).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                            </div>

                            {(adeudos && adeudos.length > 0) && (
                                <div className="mt-8 w-full flex justify-end">
                                    <div className="md:w-4/12 w-full">
                                        <div className="w-full p-6 mb-2 rounded-lg shadow-lg"
                                             style={{backgroundColor: '#33277a'}}>
                                            <div className="flex justify-between text-white text-xl font-semibold">
                                                <span className="font-bold text-sm">Redondeo:</span>
                                                <span>${(Math.ceil(totalSelected) - totalSelected).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-white text-xl font-semibold">
                                                <span className="font-bold text-sm">Total a pagar:</span>
                                                <span><NumericFormat
                                                    value={totalSelectedRounded.toFixed(2)}
                                                    displayType={'text'}
                                                    prefix={'$'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                /></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            { expiredDebts && (
                <Dialog open={openDialogPre} handler={setOpenDialogPre}>
                    <DialogHeader>Prescripción predio {clavePredio}</DialogHeader>
                    <DialogBody
                        className="pt-0 max-h-[560px] overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-xl [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-xl [&::-webkit-scrollbar-track]:bg-slate-100">
                        <Typography variant={"small"}
                                    className={`mb-2 pl-1 font-medium`}>
                            Adeudos a eliminar
                        </Typography>
                        <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                            <thead>
                            <tr>
                                {["Año", "Impuesto", "Imp. Univ.", "Recargos", "Subtotal"].map(
                                    (el) => (
                                        <th
                                            key={el}
                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                        >
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                {el}
                                            </Typography>
                                        </th>
                                    )
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            { expiredDebts.map((debt, key) => (
                                <tr key={key}>
                                    <td className='py-3 px-5 border-b border-blue-gray-50 cursor-pointer hover:bg-gray-50'>
                                        <Typography
                                            variant="small"
                                            className="text-xs font-medium text-blue-gray-600"
                                        >
                                            {debt.year}
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 border-b border-blue-gray-50 cursor-pointer hover:bg-gray-50'>
                                        <Typography
                                            variant="small"
                                            className="text-xs font-medium text-blue-gray-600"
                                        >
                                            <NumericFormat
                                                value={debt.tax.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 border-b border-blue-gray-50 cursor-pointer hover:bg-gray-50'>
                                        <Typography
                                            variant="small"
                                            className="text-xs font-medium text-blue-gray-600"
                                        >
                                            <NumericFormat
                                                value={debt.university_tax.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 border-b border-blue-gray-50 cursor-pointer hover:bg-gray-50'>
                                        <Typography
                                            variant="small"
                                            className="text-xs font-medium text-blue-gray-600"
                                        >
                                            <NumericFormat
                                                value={debt.surcharge.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 border-b border-blue-gray-50 cursor-pointer hover:bg-gray-50'>
                                        <Typography
                                            variant="small"
                                            className="text-xs font-medium text-blue-gray-600"
                                        >
                                            <NumericFormat
                                                value={debt.subtotal.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        </div>
                        <Typography variant="small" className={`mt-4 pl-1 font-medium ${notesError && 'text-red-600'}`}>
                            Motivo
                        </Typography>
                        {notesError && (
                            <Typography color="red" variant="small" className="pl-1 font-medium">
                                {notesError}
                            </Typography>
                        )}
                        <Textarea
                            placeholder="Agregue motivo de la pre-inscripción"
                            rows={6}
                            className={`mt-2 !border-blue-gray-200 focus:!border-0 focus:ring focus:ring-blue-500 focus:ring-inset ${notesError && 'ring-red-600 ring ring-inset'}`}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            error={!!notesError}
                            labelProps={{
                                className: "!border-0 before:!border-0 after:!border-0",
                            }}
                        />
                        <Typography
                            variant="small"
                            color="red"
                            className="mt-2 flex items-center gap-1 font-normal"
                        >
                            <InformationCircleIcon className='h-4 w-4'/>
                            Esta acción eliminará los adeudos con posibilidad de prescribir y no es reversible.
                        </Typography>
                    </DialogBody>
                    <DialogFooter>
                        <Button variant="text" color="red" onClick={() => setOpenDialogPre(false)} className="mr-1">
                            <span>Cancelar</span>
                        </Button>
                        <Button variant="gradient" color="green" onClick={handleDebtExpiration}>
                            <span>Confirmar</span>
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}

            <Dialog open={openEndDialog} handler={() => setOpenEndDialog(!openEndDialog)}>
                <DialogHeader>Advertencia</DialogHeader>
                <DialogBody>
                    <Typography>
                        La prescripción de las deudas ha sido realizada con éxito.
                    </Typography>
                </DialogBody>
                <DialogFooter>
                <Button
                        className="bg-green-500 text-white"
                        onClick={() => setOpenEndDialog(false)}
                    >
                        Cerrar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default Lots;
